import './Navbar.css';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import { useScroll } from '@anticatrade/shared';
import { Link } from '@anticatrade/shared';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlAvatar from '@shoelace-style/shoelace/dist/react/avatar/index.js';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface NavbarContext {
	isMenuOpen: boolean;
	setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const navbarContext = createContext<NavbarContext | undefined>(undefined);

const Navbar = () => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const { unreadMessagesCount, logout, merchant, user } = useContext(
		appContext as React.Context<AppContext>,
	);

	const { disableScroll, enableScroll } = useScroll();
	const { t } = useTranslation();
	const location = useLocation();

	const onMenuOpen = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const onMenuClose = () => {
		setIsMenuOpen(false);
	};

	const onLogout = async (e: any) => {
		e.stopPropagation();
		await logout();
	};

	useEffect(() => {
		setIsMenuOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (isMenuOpen) {
			disableScroll();
		} else {
			enableScroll();
		}
		return () => {
			enableScroll();
		};
	}, [isMenuOpen]);

	const isSuspended = merchant?.status === 'suspended';

	return (
		<navbarContext.Provider
			value={{
				isMenuOpen: isMenuOpen,
				setIsMenuOpen: setIsMenuOpen,
			}}
		>
			<div className='navbar'>
				<div className='navbar__buttons'>
					<NavbarButton route='products' isDisabled={isSuspended}>
						<SlIcon name='boxes' slot='prefix' />
						<div>{t('Catalog')}</div>
					</NavbarButton>
					<NavbarButton route='conversations' isDisabled={isSuspended}>
						<SlIcon name='envelope' slot='prefix' />
						<div>
							{t('Messages')}
							{unreadMessagesCount != null && unreadMessagesCount > 0 && (
								<span className='navbar__link-count'>{unreadMessagesCount}</span>
							)}
						</div>
					</NavbarButton>
					<NavbarButton route='statistics' isDisabled={isSuspended}>
						<SlIcon name='bar-chart-line' />
						<div>{t('Statistics')}</div>
					</NavbarButton>
					<div
						className={`navbar__button navbar__account${isMenuOpen ? ' navbar__button--selected' : ''}`}
						onClick={onMenuOpen}
					>
						<SlAvatar
							image={
								merchant?.logo
									? `${import.meta.env.VITE_MERCHANT_IMAGE_URL}/${
											merchant?.id
										}/${merchant?.logo.fileName}.webp`
									: undefined
							}
						/>
					</div>
				</div>
			</div>
			<div className={`navbar__menu${isMenuOpen ? ' navbar__menu--open' : ''}`}>
				<div className='navbar__menu-title'>
					<div className='navbar__menu-greeting'>
						<span>{t('Hi')},</span> {user?.firstName}!
					</div>
					<SlButton onClick={onMenuClose} className='navbar__menu-close'>
						<SlIcon slot='prefix' name='x-lg' />
					</SlButton>
				</div>
				<div className='navbar__menu-info'>
					<div className='navbar__menu-legal'>{merchant?.name}</div>
					<div className='navbar__menu-subscription'>
						{t('Annual plan of {count} objects').replace(
							'{count}',
							String(merchant!.subscriptionLimit),
						)}
					</div>
				</div>
				<NavbarLink route='merchant'>
					<SlIcon name='shop' />
					<div>{t('Profile')}</div>
				</NavbarLink>
				<NavbarLink route='billing'>
					<SlIcon name='credit-card' slot='prefix' />
					<div>{t('Billing')}</div>
				</NavbarLink>
				<NavbarLink route='settings'>
					<SlIcon name='gear' slot='prefix' />
					<div>{t('Settings')}</div>
				</NavbarLink>
				{merchant?.features?.includes('integrations') && (
					<NavbarLink route='integrations'>
						<SlIcon name='plug' slot='prefix' />
						<div>{t('Integrations')}</div>
					</NavbarLink>
				)}
				<div className='navbar__link' onClick={onLogout}>
					<div className='navbar__link-children'>
						<SlIcon name='box-arrow-left' slot='prefix' />
						<div>{t('Logout')}</div>
					</div>
				</div>
			</div>
			<div className={`navbar__overlay${isMenuOpen ? ' navbar__overlay--open' : ''}`} />
		</navbarContext.Provider>
	);
};

interface MobileHeaderLinkProps {
	route: string;
	children: ReactNode;
	isDisabled?: boolean;
}

const NavbarButton = ({ route, children, isDisabled }: MobileHeaderLinkProps) => {
	const location = useLocation();
	const isCurrent = location.pathname.substring(1).startsWith(route);
	const { isMenuOpen, setIsMenuOpen } = useContext(navbarContext as React.Context<NavbarContext>);

	const onClick = () => {
		setIsMenuOpen(false);
	};

	return (
		<Link path={isDisabled ? null : route} onClick={isDisabled ? undefined : onClick}>
			<div
				className={`navbar__button${
					!isMenuOpen && isCurrent && !isDisabled ? ' navbar__button--selected' : ''
				}${isDisabled ? ' navbar__button--disabled' : ''}`}
			>
				{children}
			</div>
		</Link>
	);
};

interface MobileMenuLinkProps {
	route: string;
	children: ReactNode;
}

const NavbarLink = ({ route, children }: MobileMenuLinkProps) => {
	const location = useLocation();
	const isCurrent = location.pathname.substring(1).startsWith(route);
	const { setIsMenuOpen } = useContext(navbarContext as React.Context<NavbarContext>);

	const onClick = () => {
		setIsMenuOpen(false);
	};

	return (
		<Link
			path={route}
			className={`navbar__link${isCurrent ? ' navbar__link--selected' : ''}`}
			onClick={onClick}
		>
			<div className='navbar__link-children'>{children}</div>
			<SlIcon name='chevron-right' />
		</Link>
	);
};

export { Navbar };
